import { nowtime, formatDelay } from './utils';

const defaultOptions = {
    originTime: 0,
    playbackRate: 1.0,
};

const _timeMark = Symbol('timeMark');
const _playbackRate = Symbol('playbackRate');
const _timers = Symbol('timers');
const _originTime = Symbol('originTime');
const _setTimer = Symbol('setTimer');
const _parent = Symbol('parent');
const _createTime = Symbol('createTime');

class Timeline {
    constructor(options, parent) {
        if (options instanceof Timeline) {
            parent = options;
            options = {};
        }

        options = Object.assign({}, defaultOptions, options);

        if (parent) {
            this[_parent] = parent;
        }

        // timeMark records the reference points on timeline
        // Each time we change the playbackRate or currentTime or entropy
        // A new timeMark will be generated
        // timeMark sorted by entropy
        // If you reset entropy, all the timeMarks behind the new entropy
        // should be dropped
        this[_timeMark] = [
            {
                globalTime: this.getGlobalTime(),
                localTime: -options.originTime,
                entropy: -options.originTime,
                playbackRate: options.playbackRate,
                globalEntropy: 0,
            },
        ];

        this[_createTime] = nowtime();

        if (this[_parent]) {
            this[_timeMark][0].setGlobalEntropy(this[_parent].getEntropy());
        }

        this[_originTime] = options.originTime;
        this[_playbackRate] = options.playbackRate;
        this[_timers] = new Map();
    }
    getParent() {
        return this[_parent];
    }
    getLastTimeMark() {
        return this[_timeMark][this[_timeMark].length - 1];
    }
    markTime({
        time = this.getCurrentTime(),
        entropy = this.getEntropy(),
        playbackRate = this.getPlaybackRate(),
    } = {}) {
        const timeMark = {
            globalTime: this.getGlobalTime(),
            localTime: time,
            entropy,
            playbackRate,
            globalEntropy: this.getGlobalEntropy(),
        };

        this[_timeMark].push(timeMark);
    }
    getCurrentTime() {
        const { localTime, globalTime } = this.getLastTimeMark();

        return localTime + (this.getGlobalTime() - globalTime) * this.getPlaybackRate();
    }
    setCurrentTime(time) {
        const from = this.getCurrentTime();
        const to = time;
        const timers = this[_timers];

        this.markTime({ time });
        [...timers].forEach(([id, timer]) => {
            const { isEntropy, delay, heading } = timer.time;
            const { handler, startTime } = timer;

            if (!isEntropy) {
                const endTime = startTime + delay;

                if (
                    delay === 0 ||
                    (heading !== false && (to - from) * delay < 0) ||
                    (from < endTime && endTime < to) ||
                    (from > endTime && endTime > to)
                ) {
                    handler();
                    timers.delete(id);
                }
            } else if (delay === 0) {
                handler();
                timers.delete(id);
            }
        });
        this.updateTimers();
    }
    // Both currentTime and entropy should be influenced by playbackRate.
    // If current playbackRate is negative, the currentTime should go backwards
    // while the entropy remain to go forwards.
    // Both of the initial values is set to -originTime
    getEntropy() {
        const { entropy, globalEntropy } = this.getLastTimeMark();

        return entropy + Math.abs((this.getGlobalEntropy() - globalEntropy) * this.getPlaybackRate());
    }
    getGlobalEntropy() {
        return this[_parent] ? this[_parent].getEntropy() : nowtime() - this[_createTime];
    }
    getGlobalTime() {
        if (this[_parent]) {
            return this[_parent].getCurrentTime();
        }

        return nowtime();
    }
    // change entropy will NOT cause currentTime changing but may influence the pass
    // and the future of the timeline. (It may change the result of seek***Time)
    // While entropy is set, all the marks behind will be droped
    setEntropy(entropy) {
        if (this.getEntropy() > entropy) {
            const idx = this.seekTimeMark(entropy);

            this[_timeMark].length = idx + 1;
        }
        this.markTime({ entropy });
        this.updateTimers();
    }
    fork(options) {
        return new Timeline(options, this);
    }
    seekGlobalTime(seekEntropy) {
        const idx = this.seekTimeMark(seekEntropy);
        const timeMark = this[_timeMark][idx];
        const { entropy, playbackRate, globalTime } = timeMark;

        return globalTime + (seekEntropy - entropy) / Math.abs(playbackRate);
    }
    seekLocalTime(seekEntropy) {
        const idx = this.seekTimeMark(seekEntropy);
        const timeMark = this[_timeMark][idx];

        const { localTime, entropy, playbackRate } = timeMark;

        if (playbackRate > 0) {
            return localTime + (seekEntropy - entropy);
        }

        return localTime - (seekEntropy - entropy);
    }
    seekTimeMark(entropy) {
        const timeMark = this[_timeMark];
        let l = 0;
        let r = timeMark.length - 1;

        if (entropy <= timeMark[l].entropy) {
            return l;
        }
        if (entropy >= timeMark[r].entropy) {
            return r;
        }

        // binary search
        let m = Math.floor((l + r) / 2);

        while (m > l && m < r) {
            if (entropy === timeMark[m].entropy) {
                return m;
            } else if (entropy < timeMark[m].entropy) {
                r = m;
            } else if (entropy > timeMark[m].entropy) {
                l = m;
            }
            m = Math.floor((l + r) / 2);
        }

        return l;
    }
    getPlaybackRate() {
        return this[_playbackRate];
    }
    setPlaybackRate(rate) {
        if (rate !== this.getPlaybackRate()) {
            this.markTime({ playbackRate: rate });
            this[_playbackRate] = rate;
            this.updateTimers();
        }
    }
    getPaused() {
        if (this.getPlaybackRate() === 0) return true;
        let parent = this.getParent();

        while (parent) {
            if (parent.getPlaybackRate() === 0) return true;
            parent = parent.getParent();
        }

        return false;
    }
    updateTimers() {
        const timers = [...this[_timers]];

        timers.forEach(([id, timer]) => {
            this[_setTimer](timer.handler, timer.time, id);
        });
    }
    clearTimeout(id) {
        const timer = this[_timers].get(id);

        if (timer && timer.timerID != null) {
            if (this[_parent]) {
                this[_parent].clearTimeout(timer.timerID);
            } else {
                clearTimeout(timer.timerID);
            }
        }
        this[_timers].delete(id);
    }
    clearInterval(id) {
        return this.clearTimeout(id);
    }
    clear() {
        // clear all running timers
        const timers = this[_timers];

        [...timers.keys()].forEach(id => {
            this.clearTimeout(id);
        });
    }
    /*
    setTimeout(func, {delay: 100, isEntropy: true})
    setTimeout(func, {entropy: 100})
    setTimeout(func, 100})
   */
    setTimeout(handler, time = { delay: 0 }) {
        return this[_setTimer](handler, time);
    }
    setInterval(handler, time = { delay: 0 }) {
        /* eslint-disable */
        const that = this;
        const id = this[_setTimer](function step() {
            // reset timer before handler cause we may clearTimeout in handler()
            that[_setTimer](step, time, id);
            handler();
        }, time);
        /* eslint-enable*/

        return id;
    }
    [_setTimer](handler, time, id = Symbol('timerID')) {
        time = formatDelay(time);

        const timer = this[_timers].get(id);
        let delay = 0;
        let timerID = null;
        let startTime = 0;
        let startEntropy = 0;

        if (timer) {
            this.clearTimeout(id);
            if (time.isEntropy) {
                delay = (time.delay - (this.getEntropy() - timer.startEntropy)) / Math.abs(this.getPlaybackRate());
            } else {
                delay = (time.delay - (this.getCurrentTime() - timer.startTime)) / this.getPlaybackRate();
            }
            startTime = timer.startTime;
            startEntropy = timer.startEntropy;
        } else {
            delay = time.delay / (time.isEntropy ? Math.abs(this.getPlaybackRate()) : this.getPlaybackRate());
            startTime = this.getCurrentTime();
            startEntropy = this.getEntropy();
        }

        const parent = this[_parent];
        const globalTimeout = parent ? parent.setTimeout.bind(parent) : setTimeout;
        const heading = time.heading;

        // console.log(heading, parent, delay)
        if (!parent && heading === false && delay < 0) {
            delay = Infinity;
        }

        // if playbackRate is zero, delay will be infinity.
        // For wxapp bugs, cannot use Number.isFinite yet.
        if (isFinite(delay) || parent) {
            // eslint-disable-line no-restricted-globals
            delay = Math.ceil(delay);
            if (globalTimeout !== setTimeout) {
                delay = { delay, heading };
            }
            timerID = globalTimeout(() => {
                this[_timers].delete(id);
                handler();
            }, delay);
        }

        this[_timers].set(id, {
            timerID,
            handler,
            time,
            startTime,
            startEntropy,
        });

        return id;
    }
}

export default Timeline;
